import { Button, Space, Table } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import Page from "../../Common/Page";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteRule, useFetchLeadersQualification, useFetchLeadersRules, useUpdateRule } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import { rulesList } from "./Rules/ComparisonRules";
import { getTranslatedFields } from "./StatisticFields";

const PermissionName = PermissionNames.LEADER_CRITERIA_SET;

export const rules = {
  EQUAL: "=",
  GREATER_THAN: ">",
  GREATER_THAN_EQUAL: ">=",
  LESS_THAN: "<",
  LESS_THAN_EQUAL: "<=",
};

const columnsone = ({ formatMessage, handleOpenConfirmation, handleOpenEditModal }) => {
  return [
    {
      title: formatMessage({
        id: "statistic.field",
        defaultMessage: "statisticField",
      }),
      dataIndex: "statisticField",
      render: (statisticField) => (
        <strong>
          {formatMessage({
            id: `stats.full.${statisticField}`,
          })}
        </strong>
      ),
      width: "70px",
    },
    {
      title: formatMessage({
        id: "comparison.field",
        defaultMessage: "comparisonField",
      }),
      render: (comparisonField) => (
        <strong>
          {formatMessage({
            id: `stats.full.${comparisonField}`,
          })}
        </strong>
      ),
      dataIndex: "comparisonField",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "comparison.type",
        defaultMessage: "comparisonType",
      }),
      render: (comparisonType) => <strong>{rules[comparisonType]}</strong>,
      dataIndex: "comparisonType",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "comparison.value",
        defaultMessage: "comparisonValue",
      }),
      dataIndex: "comparisonValue",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck
            permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
            permissionOptions={[PermissionOperations.UPDATE]}
          >
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenEditModal(record);
              }}
            >
              {formatMessage({
                id: "edit",
                defaultMessage: "edit",
              })}
            </StyledButton>
          </PermissionCheck>
          <PermissionCheck
            permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
            permissionOptions={[PermissionOperations.DELETE]}
          >
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const EditModal = ({ isOpen, setIsOpen, handleClose, record, handleOk }) => {
  const [comparisonValue, setComparisonValue] = useState(record.comparisonValue);
  const [comparisonField, setComparisonField] = useState(record.comparisonField);
  const [statisticField, setStatisticField] = useState(record.statisticField);
  const [comparisonType, setComparisonType] = useState(record.comparisonType);
  const { formatMessage, messages } = useIntl();
  const statisticFields = getTranslatedFields({ messages, formatMessage });

  const handleSubmit = useCallback(() => {
    handleOk({
      comparisonType,
      comparisonValue,
      comparisonField,
      statisticField,
    });
  }, [comparisonValue, comparisonField, comparisonType, statisticField, handleOk]);
  return (
    <ConfirmationModal
      title="Edit"
      setIsModalOpen={setIsOpen}
      isModalOpen={isOpen}
      handleOk={handleSubmit}
      handleCancel={handleClose}
      okText="Ok"
    >
      <div>
        <label>
          {formatMessage({
            id: "statistic.field",
            defaultMessage: "statisticField",
          })}
        </label>
        <StyledSelect
          onChange={(event) => {
            setStatisticField(event.target.value);
          }}
        >
          {statisticFields.map((option) => (
            <option selected={option.value === statisticField} value={option.value} key={option.value}>
              {option.text}
            </option>
          ))}
        </StyledSelect>
      </div>
      <div>
        <label>
          {formatMessage({
            id: "comparison.field",
            defaultMessage: "comparisonField",
          })}
        </label>
        <StyledSelect
          onChange={(event) => {
            setComparisonField(event.target.value);
          }}
        >
          {statisticFields.map((option) => (
            <option selected={option.value === comparisonField} value={option.value} key={option.value}>
              {option.text}
            </option>
          ))}
        </StyledSelect>
      </div>
      <div>
        <label>
          {formatMessage({
            id: "comparison.type",
            defaultMessage: "comparisonType",
          })}
        </label>
        <StyledSelect
          onChange={(event) => {
            setComparisonType(event.target.value);
          }}
        >
          {rulesList.map((option) => (
            <option selected={option.value === comparisonType} value={option.value} key={option.value}>
              {option.text}
            </option>
          ))}
        </StyledSelect>
      </div>
      <div>
        <label>
          {formatMessage({
            id: "comparison.value",
            defaultMessage: "comparisonValue",
          })}
        </label>
        <StyledInput
          type="number"
          value={comparisonValue}
          onChange={(event) => {
            setComparisonValue(event.target.value);
          }}
        ></StyledInput>
      </div>
    </ConfirmationModal>
  );
};

const Rules = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, leaderCriteriaId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange } = useHandlePagination();
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data, isLoading } = useFetchLeadersRules({
    include: "leaderCriteria",
    limit: 1000,
  });
  const { data: leaderCriteria } = useFetchLeadersQualification();
  const { mutate: deleteFn } = useDeleteRule();
  const { mutate: updateFn } = useUpdateRule();

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.qualifierId);
  }, [deleteFn, selected?.qualifierId]);

  const handleOpenEditModal = useCallback(
    (record) => {
      setSelected(record);
      setIsEditModalOpen(true);
    },
    [setIsEditModalOpen, setSelected],
  );

  const handleEdit = useCallback(
    (values) => {
      updateFn({
        data: {
          comparisonType: values.comparisonType,
          comparisonField: values.comparisonField,
          comparisonValue: values.comparisonValue,
          statisticField: values.statisticField,
        },
        qualifierId: selected.qualifierId,
      });
    },
    [selected?.qualifierId, updateFn],
  );

  const title = useMemo(() => {
    return formatMessage(
      {
        id: `pageTitles.organization.leader.${leaderCriteria ? "rules" : "empty"}`,
      },
      { ruleName: leaderCriteria?.name },
    );
  }, [formatMessage, leaderCriteria]);

  return (
    <Page title={title} tabTitle={title}>
      <div className={"mb-2"}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className={"mb-2"}
        >
          {isEditModalOpen && (
            <PermissionCheck
              permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
              permissionOptions={[PermissionOperations.UPDATE]}
            >
              <EditModal
                setIsOpen={setIsEditModalOpen}
                handleOk={handleEdit}
                record={selected}
                isOpen={isEditModalOpen}
                handleClose={() => {
                  setIsEditModalOpen(false);
                }}
              />
            </PermissionCheck>
          )}
          <PermissionCheck
            permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
            permissionOptions={[PermissionOperations.DELETE]}
          >
            <ConfirmationModal
              title="Delete Rule"
              setIsModalOpen={setShowConfirmation}
              isModalOpen={showConfirmation}
              handleOk={handleDelete}
              handleCancel={() => {
                setSelected({});
              }}
              okButtonProps={{
                danger: true,
              }}
              okText="Delete"
            >
              <div>{`You are about to delete ${selected.statisticField}. Is this OK?`}</div>
            </ConfirmationModal>
          </PermissionCheck>
          <strong>Rules</strong>
          <PermissionCheck
            permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
            permissionOptions={[PermissionOperations.CREATE]}
          >
            <Space>
              <Link
                to={`/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${leaderCriteriaId}/qualifiers/manage/`}
              >
                <Button type="primary" icon={<i className="fas fa-plus mr-1" />}>
                  Rule
                </Button>
              </Link>
            </Space>
          </PermissionCheck>
        </div>
        <Table
          loading={isLoading}
          size={"small"}
          columns={columnsone({
            customerId,
            sport,
            organizationId,
            formatMessage,
            handleOpenConfirmation,
            handleOpenEditModal,
          })}
          rowKey={"qualifierId"}
          dataSource={data}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            position: ["bottomCenter"],
            total: data?.length || 0,
            onChange: handlePaginationChange,
            showTotal: (total, range) => <PaginationComponent pageSize={pageSize} total={total} range={range} />,
          }}
        />
      </div>
    </Page>
  );
};

const StyledInput = styled.input`
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const StyledSelect = styled.select`
  display: block;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMAAAADACAQAAAD41aSMAAAAIGNIUk0AAHomAACAhAAA+gAAAIDoAAB1MAAA6mAAADqYAAAXcJy6UTwAAAACYktHRAD/h4/MvwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAxtJREFUeNrt3M1xE0EQhuHeBCAAiMM4AbLhyok/GQdAPDYhkAYJkAEHcGGMJO9Kuz3d+HmrOFD4QH3PxbZmZnobGtkEAAAAAQAgAAAEAIAAABAAAAIAQAAACAAAAQAgAAAEAIAAABAAAAIAQAAACAAAAQAgAAAEAIAAABAAAAIAQAAACAAAAQAgAAAEAIAAABAAAAIAQACeKMAUL+J7sSVejvkfjQCY4jrexOv4Vmj+y7iJL7F7CgBTXMe7iPhRiOAybuJZRFzlE2QD3M0fhQju5o8RBLkAU3yO9/f+XoHg/vwDCDIBHs5fgeDh/OkEeQD75h9NsG/+ZIIsgCmu4sOBfxtFcGj+VIIcgGPzjyI4Nn8iQQbAY/OPIHgVt0fnTyPYHmCKXXyc8XWZBHPmTyLYHuBq1vyZBHPnTyHYHmAXn2Z/bQbBkvn/C4BaBMXmz/ouqApBufnzfg6oQFBw/syfhEcTlJw/93dBIwmKzp/929BRBGXnz/88YARB4flHfCKWTXARX+vOP+Yz4UyC4vOPOhWRRVB+/nHngjIIGsw/8mDW1gQt5h97Mm5Lgibzjz6auBVBm/nHnw3dgqDR/BUO565N0Gr+Gqej1yS4iNt43mf+KsfT1yJoN3+d+wFrEDScv9IFjXMJWs5f64bMOQRN5692RelUgrbz17sjdgpB4/krXtJbSjB1nr/mLcllBNF5/qrXVJcQROf5694TXp+g5PyVL2qvS1B0/to35dcjKDt/9acK1iEoPH/9tyLOJyg9f4fHOs4jKD5/j9dSTicoP3+X52pOI2gwf5/3gpYTtJi/04NNywiazN/rxaz5BG3m7/Zk2TyCRvP3ezPucYJW83d8tO84QbP5e76aeJig3fxdn63cT9Bw/r7vhv5L0HL+zg+3/k3QdP7eL+f+IWg7f/eni38RNJ6//9vRu99/AAgAAAEAIAAABACAAAAQAAACAEAAAAgAAAEAIAAABACAAAAQAAAAbAAAgAAAEAAAAgBAAAAIAAABACAAAAQAgAAAEAAAAgBAAAAIAAABACAAAAQAgAAAEICO/QRQTDqQX8PqIQAAAABJRU5ErkJggg==);
  background-repeat: no-repeat, repeat;
  background-position: right 0.7em top 50%, 0 0;
  background-size: 1.5em auto, 100%;
  padding: 5px 35px 5px 10px;
  max-width: 200px;
`;

export default Rules;
